import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faReceipt, faGift } from '@fortawesome/free-solid-svg-icons';
import ExpenseForm from '../forms/ExpenseForm';
import SponsorForm from '../forms/SponsorForm';
import { useTranslation } from 'react-i18next';
import { Expense, ShareRatios, SponsorExpense } from '../../types';

interface AddEntrySheetProps {
    open: boolean;
    onClose: () => void;
    eventId: string;
    participants: { [userId: string]: string };
    currentUser: string;
    /** Passed through to the expense form, which opens its split picker on
        whoever this person split with last. */
    expenses?: (Expense | SponsorExpense)[];
    /** For the pickers' per-person preview — see ParticipantPicker. */
    shareRatios?: ShareRatios;
}

/**
 * The add-expense / add-sponsor forms as a sheet, for screens too narrow to run
 * the two-column layout.
 *
 * On a phone the form column stacks above the list, which puts the whole expense
 * list between "what did I just enter" and "enter the next one" — every addition
 * costs a scroll up and a scroll back. As a sheet the form comes to the thumb
 * instead, and the list stays where it was.
 *
 * It keeps itself open after a successful add: entering expenses is a run of
 * several, not one, and the form resets itself for the next one.
 *
 * Below `sm` it covers the screen outright rather than stopping short of the top
 * edge. The strip a native sheet leaves there is a target you drag it away by;
 * in a browser it is only unreachable page behind an undismissable panel.
 */
const AddEntrySheet: React.FC<AddEntrySheetProps> = ({
                                                         open,
                                                         onClose,
                                                         eventId,
                                                         participants,
                                                         currentUser,
                                                         expenses,
                                                         shareRatios,
                                                     }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'expense' | 'sponsor'>('expense');

    // Escape closes, and the page behind must not scroll under the sheet — on iOS
    // a scrollable page behind a full-height overlay is what makes the overlay
    // feel like it slid off.
    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="glass-sheet-overlay"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={t('event.expenseSection.addEntry')}
        >
            <div className="glass-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="glass-sheet-header">
                    {/* No grab handle: it promised a swipe-to-dismiss this sheet
                        never had. On a phone the sheet now takes the whole screen
                        and the X below is the way out. */}
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                            {t('event.expenseSection.addEntry')}
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="glass-header-back !p-2 flex-shrink-0"
                            aria-label={t('event.expenseSection.close')}
                        >
                            <FontAwesomeIcon icon={faTimes} className="text-sm" />
                        </button>
                    </div>

                    {/* Same segmented control as the desktop column */}
                    <div className="glass-tab-bar flex gap-1">
                        <button
                            type="button"
                            onClick={() => setActiveTab('expense')}
                            className={`glass-tab flex-1 flex items-center justify-center !py-2 ${
                                activeTab === 'expense' ? 'glass-tab-active' : ''
                            }`}
                        >
                            <FontAwesomeIcon icon={faReceipt} className="mr-2" />
                            {t('event.expenseSection.addExpense')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('sponsor')}
                            className={`glass-tab flex-1 flex items-center justify-center !py-2 ${
                                activeTab === 'sponsor' ? 'glass-tab-active' : ''
                            }`}
                        >
                            <FontAwesomeIcon icon={faGift} className="mr-2" />
                            {t('event.expenseSection.addSponsor')}
                        </button>
                    </div>
                </div>

                <div className="glass-sheet-body has-sticky-actions">
                    {activeTab === 'expense' ? (
                        <ExpenseForm
                            bare
                            eventId={eventId}
                            participants={participants}
                            shareRatios={shareRatios}
                            currentUser={currentUser}
                            expenses={expenses}
                        />
                    ) : (
                        <SponsorForm
                            bare
                            eventId={eventId}
                            participants={participants}
                            shareRatios={shareRatios}
                            currentUser={currentUser}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddEntrySheet;
